import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import PageGuide from "components/admin/PageGuide";
import PageCard from "components/admin/PageCard";
import ActionDialog from "components/admin/ActionDialog";
import GiftEditor, { GiftPreview } from "components/admin/GiftEditor";
import { useFetch, useRunner } from "components/admin/hooks";
import { useConfirm } from "components/admin/Feedback";
import { describeGift } from "components/admin/GiftEditor";
import api from "api/client";
import { useAuth } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatNumber } from "utils/format";

/** LuckyWheelManager.JACKPOT_SLOTS: 4 ô góc. */
const JACKPOT_SLOTS = [0, 6, 9, 15];

/** Loại quà màn vòng quay vẽ được: vàng, ngọc, vật phẩm, danh hiệu (PetRealmManager.GIFT_*). */
const SLOT_GIFT_TYPES = [0, 1, 2, 13];

/** Vị trí ô trên lưới 7 x 4, theo chiều kim đồng hồ từ góc trên trái như SoftLuckyWheelScreen.Cell. */
function cellOf(slot) {
  if (slot < 7) return { col: slot, row: 0 };
  if (slot < 9) return { col: 6, row: slot - 6 };
  if (slot < 16) return { col: 15 - slot, row: 3 };
  return { col: 0, row: 18 - slot };
}

const percentOf = (weight, total) => (total > 0 ? (weight * 100) / total : 0);

const percentText = (p) => {
  if (p <= 0) return "0%";
  if (p < 0.01) return "<0,01%";
  return `${p.toLocaleString("vi-VN", { maximumFractionDigits: 2 })}%`;
};

function toForm(data) {
  return {
    free_per_day: data.config.free_per_day,
    free_per_vip: data.config.free_per_vip,
    pity: data.config.pity,
    slots: data.slots.map((s) => ({ gift: s.gift, weight: s.weight })),
  };
}

function SlotDialog({ index, slot, othersTotal, onClose, onApply }) {
  const [gift, setGift] = useState(slot.gift);
  const [weight, setWeight] = useState(slot.weight);
  const jackpot = JACKPOT_SLOTS.includes(index);
  const badType = gift.length > 0 && !SLOT_GIFT_TYPES.includes(gift[0]);
  const w = Math.max(0, Number(weight) || 0);
  const livePercent = percentOf(w, othersTotal + w);
  return (
    <ActionDialog
      open
      title={`Ô ${index + 1}${jackpot ? " (ô góc, quà lớn)" : ""}`}
      description="Mỗi ô một món quà: vàng, ngọc, vật phẩm hoặc danh hiệu. Vật phẩm nên khoá giao dịch. Bấm Áp dụng rồi bấm Lưu ở trang để lưu lên máy chủ."
      color="info"
      submitText="Áp dụng"
      disabled={gift.length < 2 || badType}
      onClose={onClose}
      onSubmit={() => onApply({ gift, weight: Math.max(0, Number(weight) || 0) })}
      maxWidth="md"
    >
      <MDBox mb={2} maxWidth={360}>
        <MDInput
          fullWidth
          type="number"
          label="Trọng số (số càng lớn càng dễ trúng)"
          value={weight}
          inputProps={{ min: 0 }}
          helperText={
            w > 0
              ? `≈ ${percentText(livePercent)} mỗi lượt, khoảng 1 trên ${formatNumber(
                  Math.max(1, Math.round(100 / livePercent))
                )} lượt`
              : "0 = không bao giờ trúng"
          }
          onChange={(e) => setWeight(e.target.value)}
        />
      </MDBox>
      {badType && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Vòng quay không vẽ được loại quà này, chọn vàng, ngọc, vật phẩm hoặc danh hiệu.
        </Alert>
      )}
      <GiftEditor
        value={gift.length > 0 ? [gift] : []}
        onChange={(lines) => setGift(lines.length > 0 ? lines[lines.length - 1] : [])}
        label="Quà của ô (chỉ giữ món thêm sau cùng)"
        randomNote="Vòng quay cần quà cố định để hiện đúng icon trên ô."
      />
    </ActionDialog>
  );
}

SlotDialog.propTypes = {
  index: PropTypes.number.isRequired,
  othersTotal: PropTypes.number.isRequired,
  slot: PropTypes.shape({ gift: PropTypes.arrayOf(PropTypes.number), weight: PropTypes.number })
    .isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};

function SlotCell({ index, slot, saved, total, spins7, canEdit, onEdit }) {
  const jackpot = JACKPOT_SLOTS.includes(index);
  const unchanged = saved && JSON.stringify(saved.gift) === JSON.stringify(slot.gift);
  const changed = !saved || !unchanged || saved.weight !== slot.weight;
  const percent = percentOf(slot.weight, total);
  return (
    <MDBox
      onClick={canEdit ? onEdit : undefined}
      sx={{
        p: 1,
        minHeight: 118,
        borderRadius: 2,
        border: 2,
        borderColor: jackpot ? "warning.main" : "grey.300",
        bgcolor: jackpot ? "rgba(251, 140, 0, 0.08)" : "white",
        cursor: canEdit ? "pointer" : "default",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        "&:hover": canEdit ? { boxShadow: 3 } : undefined,
      }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="center">
        <MDTypography variant="caption" fontWeight="bold" color={jackpot ? "warning" : "text"}>
          Ô {index + 1}
          {jackpot ? " ★" : ""}
        </MDTypography>
        {changed && <Chip size="small" color="info" label="Đã sửa" sx={{ height: 18 }} />}
      </MDBox>
      <MDBox flexGrow={1}>
        <GiftPreview gift={[slot.gift]} lines={unchanged ? saved.giftLines : null} />
      </MDBox>
      <MDTypography variant="button" fontWeight="bold" color={slot.weight > 0 ? "dark" : "error"}>
        {percentText(percent)}
      </MDTypography>
      <MDTypography variant="caption" color="text">
        Trọng số {formatNumber(slot.weight)}
        {saved && spins7 > 0
          ? ` · 7 ngày trúng ${formatNumber(saved.hits7)} lần (${percentText(
              (saved.hits7 * 100) / spins7
            )})`
          : ""}
      </MDTypography>
    </MDBox>
  );
}

SlotCell.defaultProps = { saved: null };
SlotCell.propTypes = {
  index: PropTypes.number.isRequired,
  slot: PropTypes.shape({ gift: PropTypes.arrayOf(PropTypes.number), weight: PropTypes.number })
    .isRequired,
  saved: PropTypes.shape({
    gift: PropTypes.arrayOf(PropTypes.number),
    giftLines: PropTypes.array,
    weight: PropTypes.number,
    hits7: PropTypes.number,
  }),
  total: PropTypes.number.isRequired,
  spins7: PropTypes.number.isRequired,
  canEdit: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
};

function LuckyWheel() {
  const canEdit = useAuth().can(PERMISSIONS.LUCKY_WHEEL_EDIT);
  const wheel = useFetch(() => api.get("/api/admin/lucky-wheel"), []);
  const [run, busy] = useRunner();
  const confirm = useConfirm();
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (wheel.data) setForm(toForm(wheel.data));
  }, [wheel.data]);

  const data = wheel.data;
  const dirty = data && form && JSON.stringify(toForm(data)) !== JSON.stringify(form);
  const total = form ? form.slots.reduce((sum, s) => sum + (Number(s.weight) || 0), 0) : 0;
  const jackpotPercent = form
    ? JACKPOT_SLOTS.reduce((sum, i) => sum + percentOf(form.slots[i].weight, total), 0)
    : 0;
  const set = (patch) => setForm({ ...form, ...patch });
  const setSlot = (index, slot) =>
    set({ slots: form.slots.map((s, i) => (i === index ? slot : s)) });

  // Danh sách thay đổi bằng lời, dùng cho khung "chưa lưu" và hộp hỏi lại.
  const savedTotal = data ? data.slots.reduce((sum, s) => sum + s.weight, 0) : 0;
  const changes = [];
  if (data && form) {
    const c = data.config;
    if (Number(form.free_per_day) !== c.free_per_day)
      changes.push(`Lượt miễn phí mỗi ngày: ${c.free_per_day} → ${form.free_per_day}`);
    if (Number(form.free_per_vip) !== c.free_per_vip)
      changes.push(`Lượt thêm mỗi cấp VIP: ${c.free_per_vip} → ${form.free_per_vip}`);
    if (Number(form.pity) !== c.pity)
      changes.push(
        `Chắc chắn trúng ô góc sau: ${c.pity || "tắt"} → ${Number(form.pity) || "tắt"} lượt`
      );
    // Chỉ liệt kê ô được sửa trực tiếp; tỉ lệ các ô khác tự đổi theo tổng nên gom thành một dòng.
    let weightChanged = false;
    form.slots.forEach((slot, i) => {
      const old = data.slots[i];
      const giftChanged = JSON.stringify(old.gift) !== JSON.stringify(slot.gift);
      const before = percentText(percentOf(old.weight, savedTotal));
      const after = percentText(percentOf(slot.weight, total));
      if (Number(old.weight) !== Number(slot.weight)) weightChanged = true;
      if (giftChanged)
        changes.push(`Ô ${i + 1}: đổi quà thành ${describeGift([slot.gift])} (${after})`);
      else if (Number(old.weight) !== Number(slot.weight))
        changes.push(
          `Ô ${i + 1}: trọng số ${old.weight} → ${slot.weight} (tỉ lệ ${before} → ${after})`
        );
    });
    if (weightChanged) {
      const jackpotBefore = JACKPOT_SLOTS.reduce(
        (sum, i) => sum + percentOf(data.slots[i].weight, savedTotal),
        0
      );
      changes.push(
        `Tỉ lệ các ô còn lại tự đổi theo; trúng ô góc mỗi lượt: ${percentText(
          jackpotBefore
        )} → ${percentText(jackpotPercent)}`
      );
    }
  }

  // Rời trang khi còn thay đổi chưa lưu thì trình duyệt hỏi lại.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const save = async () => {
    const ok = await confirm({
      title: "Lưu vòng quay",
      message: [
        "Áp dụng ngay cho MỌI người chơi từ lượt quay kế tiếp:",
        "",
        ...changes.slice(0, 12).map((c) => `• ${c}`),
        changes.length > 12 ? `… và ${changes.length - 12} thay đổi nữa` : null,
      ]
        .filter((line) => line !== null)
        .join("\n"),
      color: "warning",
      confirmText: "Lưu và áp dụng",
    });
    if (!ok) return;
    const { ok: saved, result } = await run(
      () =>
        api.put("/api/admin/lucky-wheel", {
          free_per_day: Number(form.free_per_day) || 0,
          free_per_vip: Number(form.free_per_vip) || 0,
          pity: Number(form.pity) || 0,
          slots: form.slots,
        }),
      "Đã lưu vòng quay, áp dụng từ lượt quay kế"
    );
    if (saved) wheel.setData(result);
  };

  const actions = (
    <MDBox display="flex" gap={1}>
      {canEdit && dirty && (
        <MDButton size="small" color="white" disabled={busy} onClick={() => setForm(toForm(data))}>
          <Icon>undo</Icon>&nbsp;Bỏ sửa
        </MDButton>
      )}
      {canEdit && (
        <MDButton size="small" color="white" disabled={!dirty || busy} onClick={save}>
          <Icon>save</Icon>&nbsp;Lưu
        </MDButton>
      )}
      <Tooltip title="Tải lại (bỏ phần chưa lưu)">
        <MDButton size="small" color="white" onClick={() => wheel.reload()}>
          <Icon>refresh</Icon>
        </MDButton>
      </Tooltip>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="lucky-wheel" />
        {wheel.error && <Alert severity="error">{wheel.error}</Alert>}
        {data && form && (
          <>
            <MDBox mb={3}>
              <PageCard
                title="Lượt quay & bảo hiểm"
                subtitle="Chỉ quay bằng lượt miễn phí, reset lúc 0h giờ máy chủ"
                color="warning"
                actions={actions}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      fullWidth
                      type="number"
                      label="Lượt miễn phí mỗi ngày (mọi người)"
                      value={form.free_per_day}
                      disabled={!canEdit}
                      inputProps={{ min: 0, max: 20 }}
                      onChange={(e) => set({ free_per_day: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      fullWidth
                      type="number"
                      label="Lượt thêm mỗi cấp VIP"
                      value={form.free_per_vip}
                      disabled={!canEdit}
                      inputProps={{ min: 0, max: 5 }}
                      helperText={`VIP 1: ${
                        (Number(form.free_per_day) || 0) + (Number(form.free_per_vip) || 0)
                      } lượt · VIP 15: ${
                        (Number(form.free_per_day) || 0) + 15 * (Number(form.free_per_vip) || 0)
                      } lượt`}
                      onChange={(e) => set({ free_per_vip: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MDInput
                      fullWidth
                      type="number"
                      label="Chắc chắn trúng ô góc sau (lượt)"
                      value={form.pity}
                      disabled={!canEdit}
                      inputProps={{ min: 0 }}
                      helperText="Quay chừng này lượt liên tiếp chưa trúng ô góc thì lượt kế chắc chắn trúng. 0 = tắt"
                      onChange={(e) => set({ pity: e.target.value })}
                    />
                  </Grid>
                </Grid>
                <MDBox display="flex" gap={1} flexWrap="wrap" mt={2}>
                  <Chip label={`Hôm nay: ${formatNumber(data.stats.spinsToday)} lượt quay`} />
                  <Chip label={`7 ngày: ${formatNumber(data.stats.spins7)} lượt`} />
                  <Chip label={`7 ngày: ${formatNumber(data.stats.players7)} người quay`} />
                  <Chip
                    label={`7 ngày: ${formatNumber(
                      data.stats.pity7
                    )} lần trúng ô góc nhờ "chắc chắn trúng"`}
                  />
                </MDBox>
              </PageCard>
            </MDBox>

            <PageCard
              title="18 ô quà"
              subtitle={
                canEdit
                  ? "Xếp như trong game, bấm vào ô để đổi quà / trọng số rồi bấm Lưu"
                  : "Xếp như trong game"
              }
              color="info"
              actions={actions}
            >
              {dirty && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  action={
                    <MDBox display="flex" gap={1}>
                      <MDButton
                        size="small"
                        variant="text"
                        color="secondary"
                        onClick={() => setForm(toForm(data))}
                      >
                        Bỏ thay đổi
                      </MDButton>
                      <MDButton
                        size="small"
                        variant="gradient"
                        color="warning"
                        disabled={busy || total <= 0}
                        onClick={save}
                      >
                        Lưu
                      </MDButton>
                    </MDBox>
                  }
                >
                  <b>{changes.length} thay đổi chưa lưu</b> — người chơi chưa bị ảnh hưởng cho tới
                  khi bấm Lưu.
                  <MDBox component="ul" m={0} pl={2.5}>
                    {changes.slice(0, 6).map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                    {changes.length > 6 && <li>… và {changes.length - 6} thay đổi nữa</li>}
                  </MDBox>
                </Alert>
              )}
              <MDTypography variant="caption" color="text" display="block" mb={1}>
                ★ Ô góc (viền cam) là quà lớn, được tính cho &quot;chắc chắn trúng&quot;. Số % là tỉ
                lệ trúng mỗi lượt; số 7 ngày là thực tế người chơi đã trúng. Màn hẹp thì kéo ngang
                để xem đủ.
              </MDTypography>
              {total <= 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Tổng trọng số bằng 0, vòng quay sẽ báo bảo trì.
                </Alert>
              )}
              <MDBox sx={{ overflowX: "auto" }}>
                <MDBox
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, minmax(120px, 1fr))",
                    gridTemplateRows: "repeat(4, auto)",
                    gap: 1,
                    minWidth: 7 * 128,
                  }}
                >
                  {form.slots.map((slot, i) => {
                    const { col, row } = cellOf(i);
                    return (
                      <MDBox key={i} sx={{ gridColumn: col + 1, gridRow: row + 1 }}>
                        <SlotCell
                          index={i}
                          slot={slot}
                          saved={data.slots[i]}
                          total={total}
                          spins7={data.stats.spins7}
                          canEdit={canEdit}
                          onEdit={() => setEditing(i)}
                        />
                      </MDBox>
                    );
                  })}
                  <MDBox
                    sx={{
                      gridColumn: "2 / 7",
                      gridRow: "2 / 4",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      borderRadius: 2,
                      bgcolor: "grey.100",
                      p: 2,
                    }}
                  >
                    <MDTypography variant="h5">Vòng Quay May Mắn</MDTypography>
                    <MDTypography variant="button" color="text">
                      Tổng trọng số: <b>{formatNumber(total)}</b>
                    </MDTypography>
                    <MDTypography variant="button" color="text">
                      Tỉ lệ trúng ô góc mỗi lượt: <b>{percentText(jackpotPercent)}</b>
                    </MDTypography>
                    {jackpotPercent > 0 && (
                      <MDTypography variant="caption" color="text">
                        Trung bình ~{formatNumber(Math.round(100 / jackpotPercent))} lượt trúng một
                        lần ô góc
                        {Number(form.pity) > 0
                          ? `, chắc chắn trúng sau ${formatNumber(form.pity)} lượt`
                          : ""}
                      </MDTypography>
                    )}
                  </MDBox>
                </MDBox>
              </MDBox>
            </PageCard>
          </>
        )}
      </MDBox>
      {editing !== null && form && (
        <SlotDialog
          index={editing}
          slot={form.slots[editing]}
          othersTotal={total - (Number(form.slots[editing].weight) || 0)}
          onClose={() => setEditing(null)}
          onApply={(slot) => {
            setSlot(editing, slot);
            setEditing(null);
          }}
        />
      )}
      <Footer />
    </DashboardLayout>
  );
}

export default LuckyWheel;
